<script setup>
  import { ref, computed, onMounted } from "vue";

  /* SIDEBAR */
  import Sidebar from "@/components/WEB/sidebar.vue";

  /* COMPONENTS */
  import WebsiteSettings from "@/components/WEB/WebsiteSettings.vue";
  import ThemeSettings from "@/components/WEB/ThemeSettings.vue";
  import HeroSettings from "@/components/WEB/HeroSettings.vue";
  import dashboard from "@/components/WEB/HeroDasboard.vue";
  import Notifications from "@/components/WEB/NoticeCms.vue";
  import SiteWidgetsManager from "@/components/WEB/SiteWidgetsManager.vue";
  import Academic from "@/components/WEB/Academic.vue";
  import Campus from "@/components/WEB/Campus.vue";
  import about from "@/components/WEB/SchoolAbout.vue";
  import Principal from "@/components/WEB/Principal.vue";
  import Futures from "@/components/WEB/Futures&Parents.vue";
  import ProspectusFooterEditor from "@/components/WEB/ProspectusFooterEditor.vue";

  /* =========================================
   LOCAL STORAGE
========================================= */
  const STORAGE_KEY = "educanium_active_sidebar_tab";

  /* =========================================
   ACTIVE PAGE
========================================= */
  const activePage = ref("dashboard");

  /* =========================================
   COMPONENT MAP
========================================= */
  const componentMap = {
    "website-settings": WebsiteSettings,
    "theme-settings": ThemeSettings,
    "hero-section": HeroSettings,
    dashboard: dashboard,
    notifications: Notifications,
    facilities: SiteWidgetsManager,
    academics: Academic,
    "campus-infrastructure": Campus,
    "about-school": about,
    leadership: Principal,
    events: Futures,
    "social-links": ProspectusFooterEditor,
  };

  /* =========================================
   CURRENT COMPONENT
========================================= */
  const currentComponent = computed(() => {
    return componentMap[activePage.value] || WebsiteSettings;
  });

  /* =========================================
   RESTORE ACTIVE PAGE
========================================= */
  onMounted(() => {
    try {
      const savedPage = localStorage.getItem(STORAGE_KEY);

      // Only restore if the saved page actually exists
      // in the component map.
      if (savedPage && componentMap[savedPage]) {
        activePage.value = savedPage;
      }
    } catch (error) {
      console.error("Failed to restore active sidebar tab:", error);
    }
  });

  /* =========================================
   CHANGE PAGE
========================================= */
  const changePage = (page) => {
    // Prevent invalid page names
    if (!componentMap[page]) {
      console.warn(`Invalid page: ${page}`);
      return;
    }

    // Change active component
    activePage.value = page;

    // Save selected tab
    try {
      localStorage.setItem(STORAGE_KEY, page);
    } catch (error) {
      console.error("Failed to save active sidebar tab:", error);
    }
  };
</script>

<template>
  <!-- FULL PAGE WRAPPER -->
  <div
    class="h-full w-full min-h-0 bg-surface-50 flex items-stretch overflow-hidden"
  >
    <!-- SIDEBAR -->
    <aside class="shrink-0 h-full min-h-0 flex flex-col z-30 overflow-hidden">
      <Sidebar
        :active-page="activePage"
        @change-page="changePage"
        class="h-full"
      />
    </aside>

    <!-- MAIN SCROLL CONTAINER -->
    <main
      class="flex-1 min-w-0 min-h-0 h-full overflow-y-auto overflow-x-hidden p-3 sm:p-5 lg:p-8 custom-scrollbar"
    >
      <div class="w-full max-w-6xl mx-auto pb-12">
        <component :is="currentComponent" />
      </div>
    </main>
  </div>
</template>

<style scoped>
  /* Main content scrollbar */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 9999px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  /* Prevent horizontal scrollbar */
  .custom-scrollbar::-webkit-scrollbar:horizontal {
    display: none;
  }
</style>
